/**
 * Fit form — question dataset, answer types, and tier recommendation.
 *
 * Display copy is pulled from i18n catalogs (`fit.questions`, `fit.tiers`,
 * `fit.summary`, `fit.proposalEmail`). Option `value` identifiers and tier
 * keys are stable English identifiers that never change between locales.
 */

import type { TFunction } from "i18next";

export type AnswerKey = "stage" | "pain" | "timeline" | "budget";
export type Answers = Partial<Record<AnswerKey, string>>;

export interface FitOption {
  value: string;
  label: string;
  echo: string;
}

export interface FitQuestion {
  key: AnswerKey;
  eyebrow: string;
  title: string;
  intro: string;
  options: FitOption[];
}

/** Number of questions (constant across locales). */
export const QUESTION_COUNT = 4;

/**
 * Resolve the localized list of questions from the i18n catalog.
 * Call from a component that already has access to `t`.
 */
export function getQuestions(t: TFunction): FitQuestion[] {
  return t("fit.questions", { returnObjects: true }) as FitQuestion[];
}

export type TierKey = "Audit" | "Core" | "Premium";

export interface TierRecommendation {
  tier: TierKey | null;
  pitch: string;
}

export function recommendTier(answers: Answers, t: TFunction): TierRecommendation {
  switch (answers.budget) {
    case "under-5k":
      return { tier: "Audit", pitch: t("fit.tiers.audit") };
    case "5-25k":
      return { tier: "Core", pitch: t("fit.tiers.core") };
    case "25k-plus":
      return { tier: "Premium", pitch: t("fit.tiers.premium") };
    case "not-sure":
    default:
      return { tier: null, pitch: t("fit.tiers.notSure") };
  }
}

/**
 * Bare bullet summary of the user's answers. Used as Cal booking notes.
 * Falls back to "—" for any unanswered question (shouldn't happen if the
 * form is gated, but defensive — the user can step through with Back).
 */
export function buildAnswerSummary(
  answers: Answers,
  tier: TierRecommendation,
  t: TFunction,
): string {
  const questions = getQuestions(t);
  const labelFor = (k: AnswerKey): string => {
    const q = questions.find((x) => x.key === k);
    const v = answers[k];
    return q?.options.find((o) => o.value === v)?.label ?? t("fit.summary.dash");
  };
  return [
    `· ${t("fit.summary.stage")}: ${labelFor("stage")}`,
    `· ${t("fit.summary.pain")}: ${labelFor("pain")}`,
    `· ${t("fit.summary.timeline")}: ${labelFor("timeline")}`,
    `· ${t("fit.summary.budget")}: ${labelFor("budget")}`,
    tier.tier
      ? `· ${t("fit.summary.recommendedTier")}: ${tier.tier}`
      : `· ${t("fit.summary.tierTbd")}`,
  ].join("\n");
}

/**
 * Friendly email draft for the "Request a proposal" mailto. Wraps the
 * answer summary with a greeting, a bracketed placeholder for the user
 * to describe their project, and a sign-off. The bracketed line is the
 * placeholder — the user replaces it before sending.
 */
export function buildProposalEmailBody(
  answers: Answers,
  tier: TierRecommendation,
  t: TFunction,
): string {
  return [
    t("fit.proposalEmail.greeting"),
    "",
    t("fit.proposalEmail.intro"),
    "",
    buildAnswerSummary(answers, tier, t),
    "",
    t("fit.proposalEmail.placeholder"),
    "",
    t("fit.proposalEmail.signoff"),
    "",
  ].join("\n");
}
